<template>
  <v-container fluid grid-list-xl>
    <v-card raised>
        <v-card-title class="display-1">
          Vending Machine Monthly Reports
        </v-card-title>

      <data-filter
        :loading="loading"
        :selectables="selectables"
        :filters="filters"
        :hasActionButton="false"
        @updateFilters="updateFilters"
      ></data-filter>
    </v-card>

    <franchisee-report
      v-if="report"
      :report="report"
    ></franchisee-report>
  </v-container>
</template>

<script>
  import FranchiseeReport from '@/components/Rental/FranchiseeReports/FranchiseeReport'
  import franchiseeMachineService from '@/services/rental/franchiseeMachine'
  import DataFilter from '@/components/Common/DataFilter'
  import moment from 'moment-timezone'
  import _ from 'lodash'

  export default {
    components: {
      FranchiseeReport,
      DataFilter
    },
    data () {
      return {
        loading: false,
        report: null,
        franchiseeMachines: [],
        selectables: {
          franchiseeMachines: [],
          periods: [],
        },
        filterValues: {},
        filters: {
          franchisee_machine_id: {
            items: 'franchiseeMachines',
            text: 'Vending Machine',
            value: null,
            clearable: false,
          },
          period: {
            items: 'periods',
            text: 'Period',
            value: null,
            clearable: false,
          },
        },
      }
    },
    created () {
      this.getLocations()
    },
    methods: {
      extractLocation (franchiseeMachine) {
        let location = null
        if (franchiseeMachine.current_franchisee_location) {
          location = franchiseeMachine.current_franchisee_location.pivot.location
        }
        return location
      },

      async getLocations () {
        this.loading = true
        const { data } = await franchiseeMachineService.getAll()
        this.franchiseeMachines = data.data
        this.selectables['franchiseeMachines'] = this.franchiseeMachines.map((franchiseeMachine) => {
          let location = this.extractLocation(franchiseeMachine)
          return {
            text: franchiseeMachine.franchisee_machine_code + (location ? ' (' + location.name + ')' : ''),
            value: franchiseeMachine.id,
            align: 'left',
          }
        })
        this.loading = false
      },
      updateFilters (filters) {
        this.filterValues = filters
        this.selectables['periods'] = []
        if (filters.franchisee_machine_id) {
          let franchiseeMachine = this.franchiseeMachines.find(franchiseeMachine => franchiseeMachine.id === filters.franchisee_machine_id)
          if (franchiseeMachine.franchisee_machine_reports) {
            this.selectables['periods'] = franchiseeMachine.franchisee_machine_reports.map((report) => {
              return {
                text: moment(report.period, 'YYYYMM').format('MMM YYYY'),
                value: report.id,
                align: 'left'
              }
            })
          }
        }
        if (filters.period) {
          this.showReport()
        }
      },
      showReport () {
        let franchiseeMachine = this.franchiseeMachines.find(franchiseeMachine => franchiseeMachine.id === this.filterValues.franchisee_machine_id)
        let report = franchiseeMachine.franchisee_machine_reports.find(report => report.id === this.filterValues.period)
        let periodStartName = moment.unix(report.period_start).format('MM/DD/YYYY')
        let periodEndName = moment.unix(report.period_end).format('MM/DD/YYYY')

        let totalCostAndExpense = _.round(+report.cost_product + +report.cost_rent + (+report.cost_replenishment || 0) + +report.cost_transaction + +report.cost_internet, 2)
        let totalRevenue = _.round(+report.income_sales, 2)
        let adjustmentTotal = _.round(+report.adjustment_total, 2)
        let gritusProfit = _.round(+report.profit_gritus, 2)
        let franchiseeProfit = _.round(+report.profit_franchisee, 2)
        let profit = _.round(totalRevenue - totalCostAndExpense, 2)
        let totalProceed = _.round(franchiseeProfit + adjustmentTotal, 2)
        let balanceCurrent = _.round(report.balance_current, 2)
        let balanceBroughtForward = _.round(report.balance_brought_forward, 2)
        let balanceWithdrawn = _.round(report.balance_withdrawn, 2)

        report = franchiseeMachine.franchisee_machine_reports.find(report => report.id === this.filterValues.period)
        report.franchiseeMachine = franchiseeMachine

        let location = this.extractLocation(franchiseeMachine)
        this.report = {
          locationName: (location ? location.name : '-'),
          locationAddress: (location ? location.address : '-'),
          periodStartName,
          periodEndName,
          content: [
            // Account Balance
            {
              topic: {
                header: 'Account Balance',
                value: balanceCurrent,
              },
              entries: [
                {
                  header: 'Proceeds this month',
                  value: totalProceed,
                },
                {
                  header: 'Balance withdrawn',
                  value: balanceWithdrawn,
                  show: balanceWithdrawn,
                },
                {
                  header: 'Balance Brought Forward',
                  value: balanceBroughtForward,
                },
              ]
            },
            // Revenue
            {
              topic: {
                header: 'Revenue'
              },
              entries: [
                {
                  header: 'Merchandise Sales',
                  value: report.income_sales,
                  show: report.income_sales
                },
                {
                  header: 'Sales Promotion Services, Slotting Services & Other Revenue',
                  value: report.income_other,
                  show: report.income_other > 0
                },
                {
                  header: 'Total Revenue (A)',
                  value: totalRevenue,
                  type: 'subtotal'
                }
              ]
            },
            // Cost & Expenses
            {
              topic: {
                header: 'Cost & Expenses'
              },
              entries: [
                {
                  header: 'Cost of Goods Sold',
                  value: report.cost_product
                },
                {
                  header: 'Operation Cost',
                  value: report.cost_replenishment
                },
                {
                  header: 'Rent',
                  hint: report.rent_clause,
                  value: report.cost_rent
                },
                {
                  header: 'Transaction Fee',
                  hint: '0.5% - 5% of Merchandise Sales',
                  value: report.cost_transaction
                },
                {
                  header: 'Internet Fee',
                  value: report.cost_internet
                },
                {
                  header: 'Total Cost & Expenses (B)',
                  value: totalCostAndExpense,
                  type: 'subtotal'
                }
              ]
            },
            // Net Profit (A - B)
            {
              topic: {
                header: 'Net Profit (A - B)',
                value: profit,
              },
            },
            // Profit Share
            {
              topic: {
                header: 'Profit Share'
              },
              entries: [
                {
                  header: 'Gritus Share',
                  hint: (100 - report.franchisee_share) + '% of Net Profit',
                  value: gritusProfit,
                  type: 'subtotal'
                },
                {
                  header: 'Client Share (C)',
                  hint: report.franchisee_share + '% of Net Profit',
                  value: franchiseeProfit,
                  type: 'subtotal'
                }
              ]
            },
            // Adjustment
            {
              topic: {
                header: 'Adjustment'
              },
              entries: [
                {
                  header: 'Adjustment (D)',
                  value: adjustmentTotal,
                  hint: report.adjustment_total_remark,
                  type: 'subtotal'
                },
              ]
            },
            { // Total Proceeds
              topic: {
                header: 'Total Proceeds',
                value: totalProceed,
                type: 'subtotal'
              },
            },

          ]
        }
      }
    },
  }
</script>



// WEBPACK FOOTER //
// src/components/Rental/FranchiseeReports/Overview.vue